/**
 * Funil de entrega, no estilo "Colunas" do funil da Dashboard: etapas numeradas, altura
 * proporcional à RAIZ do volume (proporcional ao volume, a última etapa sumiria) e a passagem
 * entre elas na coluna do meio.
 *
 * Nenhuma taxa é calculada aqui: entrega, abertura efetiva, clique efetivo e clique sobre abertura
 * já vêm da API, cada uma com seu denominador. A tela só escolhe qual mostrar em cada degrau.
 */
import { c, v } from '@/components/v2/theme';
import type { Hue } from '@/components/v2/theme';
import type { Metrics } from '@/types/email-v1';
import { Block } from '../Block';
import type { Fmtr } from '../../lib/format';

export function Funnel({ totals, fmt }: { totals: Metrics; fmt: Fmtr }) {
    const s = totals.sends;
    const r = totals.rates;
    const steps: {
        n: string; l: string; value: number; hue: Hue | null; people?: boolean;
        pass?: { caption: string; rate: number | null };
        foot: string; loss?: number;
    }[] = [
        { n: '01', l: 'Enviados', value: s.sent, hue: null, foot: 'Início do funil' },
        { n: '02', l: 'Entregues', value: s.delivered, hue: 'cyan', pass: { caption: 'de enviados', rate: r.deliveryRate }, foot: `${fmt.pct(r.deliveryRate)} do início`, loss: s.sent - s.delivered },
        { n: '03', l: 'Abriram', value: s.uniqueOpens, hue: 'violet', people: true, pass: { caption: 'de entregues', rate: r.openRate }, foot: `${fmt.pct(r.effectiveOpenRate)} do início`, loss: s.delivered - s.uniqueOpens },
        { n: '04', l: 'Clicaram', value: s.uniqueClicks, hue: 'pink', people: true, pass: { caption: 'das aberturas', rate: r.clickToOpenRate }, foot: `${fmt.pct(r.effectiveClickRate)} do início`, loss: s.uniqueOpens - s.uniqueClicks },
    ];

    const first = Math.max(1, steps[0].value);
    const tpl = steps.map(() => 'minmax(0,1fr)').join(' 78px ');

    return (
        <Block
            title="Funil de entrega"
            sub="4 etapas · altura proporcional à raiz do volume"
            note={(
                <>
                    As duas últimas etapas são <strong style={{ fontWeight: 600 }}>pessoas distintas</strong>, não eventos:
                    foram {fmt.int(s.opens)} aberturas e {fmt.int(s.clicks)} cliques no total. Por isso o funil não fecha
                    somando eventos.
                </>
            )}
        >
            <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: tpl, alignItems: 'stretch', minWidth: steps.length * 150 }}>
                    {steps.map((step) => {
                        const h = Math.sqrt(Math.max(0, step.value) / first) * 78;
                        const hPx = Math.min(150, Math.max(56, (h / 100) * 190));
                        const bg = step.hue ? c(step.hue, 0.15) : v('chip');
                        const bd = step.hue ? c(step.hue, 0.45) : v('border-2');
                        const top = step.hue ? c(step.hue) : v('line');
                        return (
                            <div key={step.n} style={{ display: 'contents' }}>
                                {step.pass && (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, paddingTop: 34, height: 224, textAlign: 'center' }}>
                                        <span style={{ fontSize: 10, letterSpacing: '0.04em', textTransform: 'uppercase', color: v('faint'), lineHeight: 1.25 }}>
                                            {step.pass.caption}
                                        </span>
                                        <span style={{ fontSize: 12.5, fontWeight: 600, color: v('text-2'), whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                            → {fmt.pct(step.pass.rate)}
                                        </span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 24, maxWidth: '100%' }}>
                                        <span className="rv-mono" style={{ fontSize: 11, color: v('faint') }}>{step.n}</span>
                                        <span style={{ fontSize: 12.5, color: v('text-2'), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {step.l}
                                            {step.people && <span style={{ color: v('faint') }}> pessoas distintas</span>}
                                        </span>
                                    </div>
                                    <div style={{ height: 190, width: '100%', display: 'flex', alignItems: 'center' }}>
                                        <div style={{
                                            width: '100%', height: hPx, background: bg, border: `1px solid ${bd}`,
                                            borderTop: `2px solid ${top}`, borderRadius: 12, boxSizing: 'border-box',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px',
                                        }}>
                                            <span style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                {fmt.int(step.value)}
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, minHeight: 36, textAlign: 'center' }}>
                                        <span style={{ fontSize: 12, color: v('muted') }}>{step.foot}</span>
                                        {step.loss != null && step.loss > 0 && (
                                            <span style={{ fontSize: 11.5, color: v('faint') }}>−{fmt.int(step.loss)} no caminho</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </Block>
    );
}
